import { useProductSeo } from '@/components/feature/Seo';
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CartDrawer from "@/components/feature/CartDrawer";
import ProductRail from "@/pages/home/components/ProductRail";
import { fetchStoreProducts, fetchSuggestedProducts, isStoreEnabled } from "@/services/store-api";
import { products as mockProducts, type Product } from "@/mocks/products";
import { useCart } from "@/context/CartContext";
import { store } from "@/services/store";
import { formatBRL } from "@/utils/format";
import { colorAttribute, colorHex, sizeAttribute, variantImage, variantLabel, variantMaxQuantity } from "@/utils/variants";

// Página da peça: miniaturas, foto principal e compra. Funciona tanto com o
// catálogo real quanto com o de demonstração.
export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  useProductSeo(product, id);
  const [error, setError] = useState("");
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<Product[]>([]);
  const { addItem, openCart } = useCart();

  useEffect(() => {
    let active = true;
    setProduct(null);
    setError("");
    setSelectedValues({});
    setActiveImage(null);
    window.scrollTo({ top: 0, behavior: "auto" });
    const load = isStoreEnabled()
      ? fetchStoreProducts({ productId: id })
      : Promise.resolve(mockProducts.filter((item) => String(item.id) === id));
    load
      .then((items) => {
        if (!active) return;
        const found = items[0] ?? null;
        setProduct(found);
        if (!found) {
          setError("A peça que você procura pode ter saído do catálogo.");
          return;
        }
        document.title = `${found.name} | Bozzetti Store`;
        const firstVariant = found.variants?.find((variant) => variant.inStock) ?? found.variants?.[0];
        if (firstVariant) setSelectedValues(firstVariant.values);
      })
      .catch(() => {
        if (active) setError("Não foi possível carregar esta peça. Tente novamente em alguns instantes.");
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setSuggested([]);
    if (!id) return;
    const load = isStoreEnabled()
      ? fetchSuggestedProducts(id)
      : Promise.resolve(mockProducts.filter((item) => String(item.id) !== id));
    load
      .then((items) => { if (active) setSuggested(items); })
      .catch(() => { /* seção opcional: silenciosamente não mostra nada */ });
    return () => { active = false; };
  }, [id]);

  const colorName = product ? colorAttribute(product) : undefined;
  const sizeName = product ? sizeAttribute(product) : undefined;
  // Tamanho primeiro, depois cor e os demais atributos.
  const attributeNames = useMemo(
    () => Object.keys(product?.attributes ?? {}).sort((a, b) => Number(b === sizeName) - Number(a === sizeName)),
    [product, sizeName]
  );

  const selectedVariant = useMemo(() => {
    if (!product?.variants || product.variants.length === 0) return null;
    return product.variants.find((variant) => attributeNames.every((name) => variant.values[name] === selectedValues[name])) ?? null;
  }, [product, attributeNames, selectedValues]);

  // Um valor só fica disponível se existir combinação em estoque com as demais escolhas.
  const valueAvailable = (attribute: string, value: string) => {
    if (!product?.variants?.length) return true;
    return product.variants.some(
      (variant) =>
        variant.inStock &&
        variant.values[attribute] === value &&
        attributeNames.every((name) => name === attribute || !selectedValues[name] || variant.values[name] === selectedValues[name])
    );
  };

  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const displayCompareAtPrice = selectedVariant ? selectedVariant.compareAtPrice : product?.compareAtPrice;
  const displayInStock = selectedVariant ? selectedVariant.inStock : (product?.inStock ?? true);
  const activeVariantId = selectedVariant?.id ?? product?.variantId;
  const canAdd = Boolean(product) && displayInStock && Boolean(activeVariantId);

  const handleAdd = () => {
    if (!product || !canAdd || !activeVariantId) return;
    addItem({
      id: product.id,
      name: product.name,
      image: variantImage(product, selectedVariant),
      price: displayPrice,
      variantId: activeVariantId,
      variantLabel: selectedVariant ? variantLabel(selectedVariant.values) : undefined,
      maxQuantity: variantMaxQuantity(selectedVariant),
    });
    openCart();
  };

  if (error || !product) {
    return (
      <div className="bz-page">
        <Header />
        <main className="bz-status">
          {error ? (
            <>
              <h1>Peça não encontrada</h1>
              <p>{error}</p>
              <Link to="/produtos" className="bz-link">Ver a coleção</Link>
            </>
          ) : (
            <p role="status">Carregando...</p>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  const gallery = product.images && product.images.length > 0 ? product.images : [product.image];
  const mainImage = activeImage ?? variantImage(product, selectedVariant);
  const sizeQuestion = encodeURIComponent(`Olá! Tenho uma dúvida sobre o tamanho de: ${product.name}`);

  return (
    <div className="bz-page">
      <Header />
      <main>
        <div className="bz-pdp">
          <div className="bz-thumbs" aria-label="Fotos da peça">
            {gallery.map((image, index) => (
              <button key={image} type="button" aria-label={`Ver foto ${index + 1}`} aria-current={image === mainImage} onClick={() => setActiveImage(image)}>
                <img src={image} alt="" />
              </button>
            ))}
          </div>
          <div className="bz-main-photo"><img src={mainImage} alt={product.name} fetchPriority="high" /></div>

          <section className="bz-buy" aria-label="Comprar">
            <nav className="bz-crumbs" aria-label="Navegação estrutural">
              <Link to="/">Início</Link><span aria-hidden="true">»</span><Link to="/produtos">Coleção</Link>
            </nav>
            <h1>{product.name}</h1>
            <p className="bz-buy-price">
              {displayCompareAtPrice && displayCompareAtPrice > displayPrice && <s>{formatBRL(displayCompareAtPrice)}</s>}
              {formatBRL(displayPrice)}
            </p>
            <p className="bz-buy-terms">em até {product.installments}x de <b>{formatBRL(displayPrice / product.installments)}</b> no cartão</p>

            {attributeNames.map((attribute) => {
              const isColor = attribute === colorName;
              const values = product.attributes?.[attribute] ?? [];
              const showSwatches = isColor && values.every((value) => colorHex(value));
              return (
                <div key={attribute} className="bz-option">
                  <p className="bz-option-label">{attribute}{isColor && selectedValues[attribute] ? `: ${selectedValues[attribute]}` : ""}</p>
                  <div className="bz-option-values" role="radiogroup" aria-label={attribute}>
                    {values.map((value) => {
                      const props = {
                        type: "button" as const,
                        role: "radio",
                        "aria-checked": selectedValues[attribute] === value,
                        disabled: !valueAvailable(attribute, value),
                        onClick: () => { setActiveImage(null); setSelectedValues((prev) => ({ ...prev, [attribute]: value })); },
                      };
                      return showSwatches
                        ? <button key={value} {...props} className="bz-swatch" style={{ background: colorHex(value) }} aria-label={value} title={value} />
                        : <button key={value} {...props} className="bz-size">{value}</button>;
                    })}
                  </div>
                </div>
              );
            })}

            <a className="bz-size-help" href={`${store.whatsapp}?text=${sizeQuestion}`} target="_blank" rel="noopener noreferrer">Dúvida sobre o tamanho?</a>

            <button type="button" className="bz-btn" onClick={handleAdd} disabled={!canAdd}>
              {displayInStock ? "Adicionar ao carrinho" : "Indisponível"}
            </button>

            <div className="bz-details">
              {product.description && (
                <details>
                  <summary>Sobre o Produto <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" /></summary>
                  <div className="bz-details-body" dangerouslySetInnerHTML={{ __html: product.description }} />
                </details>
              )}
              <details>
                <summary>Envio <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" /></summary>
                <div className="bz-details-body">Enviamos para todo o Brasil. Prazo e valor do frete são calculados na finalização da compra.</div>
              </details>
              <details>
                <summary>Trocas e Devoluções <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" /></summary>
                <div className="bz-details-body">Você pode desistir da compra online em até 7 dias após o recebimento. <Link to="/politica-de-trocas">Veja a política completa</Link>.</div>
              </details>
            </div>
          </section>
        </div>

        <ProductRail title="Você também pode gostar" products={suggested} />
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
